import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Modal } from '@/components/dialogs/Modal';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { marginControlApi } from '@/api/services/marginControl';
import {
  MarginExposureMode,
  MarginRateApplyType,
  type FetchMarginRatesInput,
  type MarginControlOptionsDto,
  type MarginRatePreviewRowDto,
  type MarginRateRowDto,
  type MarginRateSelectionKeyDto,
} from '@/api/types/marginControl';
import { fmtRate, rowKeyOf, splitResolvedSymbols } from '../helpers';
import { ReviewDialog } from '../ReviewDialog';

// Symbol cell: ≤2 symbols inline; >2 shows the first 2 + a "+N" badge whose
// tooltip lists every resolved symbol (legacy mcRenderSymbols).
function renderSymbols(resolvedSymbol: string): ReactNode {
  const symbols = splitResolvedSymbols(resolvedSymbol);
  if (symbols.length === 0) return '—';
  if (symbols.length <= 2) return symbols.join(', ');
  const shown = symbols.slice(0, 2).join(', ');
  const rest = symbols.length - 2;
  return (
    <span>
      {shown}
      <span className="badge bg-primary-transparent ms-1" title={symbols.join(', ')}>
        +{rest}
      </span>
    </span>
  );
}

interface Props {
  options: MarginControlOptionsDto;
  applyEnabled: boolean;
  applyDisabledReason: string;
  canApply: boolean;
  onCrossReload: () => void;
}

const modeTooltip = (m: string) =>
  m === String(MarginExposureMode.NewAndExisting)
    ? 'Both existing positions and new positions are affected.'
    : 'Only new positions will be affected.';
const typeTooltip = (t: string) =>
  t === '' ? 'Choose how Value is applied.'
    : t === String(MarginRateApplyType.Multiply) ? 'Value multiplies each selected effective rate (e.g. 3 × 3 → 9).'
      : 'Value is written as the absolute margin rate.';

export function ApplyTab({ options, applyEnabled, applyDisabledReason, canApply, onCrossReload }: Props) {
  const toast = useToast();

  const [mode, setMode] = useState('0');
  const [applyType, setApplyType] = useState('');
  const [value, setValue] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [previewLogin, setPreviewLogin] = useState('');

  const [applied, setApplied] = useState<{ groupFilter: string; symbolFilter: string } | null>(null);
  const [planRows, setPlanRows] = useState<MarginRateRowDto[] | null>(null);
  const [profileId, setProfileId] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Legacy hides the selection bar entirely after a successful Apply (until the
  // next Fetch / selection change), so the user sees the applied result cleanly.
  const [barHidden, setBarHidden] = useState(false);
  const rowMap = useRef<Map<string, MarginRateRowDto>>(new Map());

  const [reviewed, setReviewed] = useState<{ rows: MarginRatePreviewRowDto[]; mode: number; applyType: number; rate: number; items: MarginRateSelectionKeyDto[]; previewLogin: number | null } | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [takeBackupOpen, setTakeBackupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const plans = useQuery({ queryKey: ['margin-control', 'plans'], queryFn: marginControlApi.getPlans, staleTime: 60_000 });

  const extra = useMemo<FetchMarginRatesInput>(
    () => ({ groupFilter: applied ? (applied.groupFilter || '*') : '*', symbolFilter: applied?.symbolFilter || undefined }),
    [applied],
  );
  const table = useServerDataTable<MarginRateRowDto, FetchMarginRatesInput>({
    queryKey: ['margin-control', 'rows'],
    fetcher: async (req) => {
      const res = await marginControlApi.fetch(req);
      res.items.forEach((r) => rowMap.current.set(rowKeyOf(r), r));
      return res;
    },
    pageSize: 5,
    extraParams: extra,
    enabled: applied != null && planRows == null,
  });

  const invalidateReview = () => setReviewed(null);

  // Rows shown: plan-loaded (client-side) or server-fetched.
  const gridRows = planRows ?? table.rows;
  const matchedCount = planRows ? planRows.length : table.totalCount;

  const toggleRow = (r: MarginRateRowDto) => {
    const key = rowKeyOf(r);
    rowMap.current.set(key, r);
    setBarHidden(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllMatched = async (checked: boolean) => {
    setBarHidden(false);
    if (!checked) { setSelected(new Set()); return; }
    if (planRows) {
      planRows.forEach((r) => rowMap.current.set(rowKeyOf(r), r));
      setSelected(new Set(planRows.map(rowKeyOf)));
      return;
    }
    // Server mode: pull every matched row so selection covers all pages.
    try {
      const all = await marginControlApi.fetch({ groupFilter: extra.groupFilter, symbolFilter: extra.symbolFilter, skipCount: 0, maxResultCount: 100000 });
      all.items.forEach((r) => rowMap.current.set(rowKeyOf(r), r));
      setSelected(new Set(all.items.map(rowKeyOf)));
    } catch {
      toast.error('Failed to select all matched securities.');
    }
  };

  const doFetch = () => {
    if (!applyEnabled) return;
    if (previewLogin.trim() && !/^\d+$/.test(previewLogin.trim())) {
      toast.error('Preview Login must be a positive MT5 login.');
      return;
    }
    setPlanRows(null);
    setProfileId('');
    setSelected(new Set());
    setBarHidden(false);
    invalidateReview();
    table.setPageIndex(0);
    setApplied({ groupFilter, symbolFilter });
  };

  const doClear = () => {
    setGroupFilter('');
    setSymbolFilter('');
    setPreviewLogin('');
    setApplyType('');
    setValue('');
    setPlanRows(null);
    setProfileId('');
    setSelected(new Set());
    setBarHidden(false);
    setApplied(null);
    invalidateReview();
  };

  const buildItems = (): MarginRateSelectionKeyDto[] =>
    [...selected]
      .map((key) => rowMap.current.get(key))
      .filter((r): r is MarginRateRowDto => r != null)
      .map((r) => ({
        group: r.group,
        path: r.path,
        expectedEffectiveInitialBuy: r.effectiveInitialBuy,
        expectedEffectiveInitialSell: r.effectiveInitialSell,
        expectedEffectiveMaintenanceBuy: r.effectiveMaintenanceBuy,
        expectedEffectiveMaintenanceSell: r.effectiveMaintenanceSell,
        expectedMarginRateInitialBuy: r.marginRateInitialBuy,
        expectedMarginRateInitialSell: r.marginRateInitialSell,
        expectedMarginRateMaintenanceBuy: r.marginRateMaintenanceBuy,
        expectedMarginRateMaintenanceSell: r.marginRateMaintenanceSell,
      }));

  const validateSettings = (): boolean => {
    if (applyType === '') { toast.error('Select a Type before entering Value.'); return false; }
    if (!(Number(value) > 0)) { toast.error('Value must be a number greater than zero.'); return false; }
    if (previewLogin.trim() && !/^\d+$/.test(previewLogin.trim())) { toast.error('Preview Login must be a positive MT5 login.'); return false; }
    return true;
  };

  const doReview = async () => {
    if (!selected.size) return;
    if (!validateSettings()) return;
    const items = buildItems();
    const pl = previewLogin.trim() ? Number(previewLogin.trim()) : null;
    try {
      const rows = await marginControlApi.preview({ mode: Number(mode), applyType: Number(applyType), rate: Number(value), previewLogin: pl, orderType: 0, items });
      setReviewed({ rows, mode: Number(mode), applyType: Number(applyType), rate: Number(value), items, previewLogin: pl });
      setShowReview(true);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to preview changes.');
    }
  };

  const doApply = async () => {
    if (!reviewed) { toast.error('Review changes before applying.'); return; }
    try {
      const res = await marginControlApi.apply({ mode: reviewed.mode, applyType: reviewed.applyType, rate: reviewed.rate, previewLogin: reviewed.previewLogin, orderType: 0, items: reviewed.items });
      const ok = res.items.filter((i) => i.success).length;
      const failed = res.items.length - ok;
      if (failed === 0) {
        toast.success(`Applied ${ok} margin rate update(s). ${res.configurationName}`.trim());
        setShowReview(false);
        setSelected(new Set());
        setBarHidden(true);
        invalidateReview();
      } else {
        toast.error(`Applied ${ok}, failed ${failed}. Refresh and review stale rows.`);
      }
      if (planRows == null) void table.refetch();
      onCrossReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to apply changes.');
    }
  };

  const doTakeBackup = async () => {
    try {
      const res = await marginControlApi.takeBackup();
      toast.success(`Backup saved: ${res.configurationName} (${res.itemCount} paths). Open Configurations to apply it later.`);
      setTakeBackupOpen(false);
      onCrossReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to take backup.');
    }
  };

  const doSavePlan = async () => {
    if (!saveName.trim()) { toast.error('Enter a profile name.'); return; }
    const items = [...selected].map((key) => { const r = rowMap.current.get(key)!; return { group: r.group, path: r.path }; });
    try {
      const res = await marginControlApi.savePlan({ name: saveName.trim(), mode: Number(mode), applyType: Number(applyType || 0), rate: Number(value || 0), items });
      toast.success(`Profile saved: ${res.name} (${res.itemCount} paths).`);
      setSaveOpen(false);
      setSaveName('');
      await plans.refetch();
      setProfileId(res.planId);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save profile.');
    }
  };

  const onProfileChange = async (v: string | null) => {
    const id = v ?? '';
    setProfileId(id);
    setBarHidden(false);
    if (!id) { setPlanRows(null); setSelected(new Set()); return; }
    try {
      const res = await marginControlApi.loadPlan(id);
      setMode(String(res.mode));
      setApplyType(String(res.applyType));
      setValue(String(res.rate));
      res.items.forEach((r) => rowMap.current.set(rowKeyOf(r), r));
      setPlanRows(res.items);
      setApplied(null);
      setSelected(new Set(res.items.map(rowKeyOf)));
      invalidateReview();
      if (res.missingCount > 0) {
        toast.error(`Loaded ${res.items.length} path(s). ${res.missingCount} saved path(s) are no longer on the platform.`);
      } else {
        toast.success(`Loaded profile "${res.name}" (${res.items.length} paths). Review, then Apply.`);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load profile.');
    }
  };

  const doDeleteProfile = async () => {
    if (!profileId) return;
    try {
      await marginControlApi.deletePlan(profileId);
      toast.success('Profile deleted.');
      setDeleteOpen(false);
      setProfileId('');
      await plans.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete profile.');
    }
  };

  // Header checkbox reflects the "all matched" scope: every matched row (across all
  // pages) is selected — not merely the rows on the current page.
  const allMatchedSelected = matchedCount > 0 && selected.size >= matchedCount;

  const columns: DataGridColumn<MarginRateRowDto>[] = [
    {
      key: 'selected', sortable: false, hideable: false, className: 'text-center',
      title: <input type="checkbox" className="form-check-input" checked={allMatchedSelected} onChange={(e) => void selectAllMatched(e.target.checked)} />,
      cell: (r) => <input type="checkbox" className="form-check-input" checked={selected.has(rowKeyOf(r))} onChange={() => toggleRow(r)} />,
    },
    { key: 'group', title: 'Group', className: 'text-start', value: (r) => r.group },
    {
      key: 'path', title: 'Path', className: 'text-start', value: (r) => r.path,
      cell: (r) => {
        const symbols = splitResolvedSymbols(r.resolvedSymbol);
        return <span title={symbols.length ? symbols.join(', ') : undefined}>{r.path}</span>;
      },
    },
    { key: 'resolvedSymbol', title: 'Symbol', className: 'text-start', value: (r) => r.resolvedSymbol, cell: (r) => renderSymbols(r.resolvedSymbol) },
    { key: 'effectiveInitialBuy', title: 'Init Buy', className: 'text-end', value: (r) => r.effectiveInitialBuy, cell: (r) => fmtRate(r.effectiveInitialBuy) },
    { key: 'effectiveInitialSell', title: 'Init Sell', className: 'text-end', value: (r) => r.effectiveInitialSell, cell: (r) => fmtRate(r.effectiveInitialSell) },
    { key: 'effectiveMaintenanceBuy', title: 'Maint Buy', className: 'text-end', value: (r) => r.effectiveMaintenanceBuy, cell: (r) => fmtRate(r.effectiveMaintenanceBuy) },
    { key: 'effectiveMaintenanceSell', title: 'Maint Sell', className: 'text-end', value: (r) => r.effectiveMaintenanceSell, cell: (r) => fmtRate(r.effectiveMaintenanceSell) },
    { key: 'initialBuySource', title: 'Source', className: 'text-start', value: (r) => r.initialBuySource },
  ];

  const hasResults = planRows != null || applied != null;
  const applyBtnDisabled = selected.size === 0 || !applyEnabled || !reviewed;

  return (
    <div>
      {!applyEnabled && (
        <div className="alert alert-warning py-2" role="alert">
          {applyDisabledReason || 'Group Margin Rate Control is disabled in Tenant Configuration.'}
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <i className="fe fe-sliders me-2" />
          <span className="fw-semibold">Margin Rate Settings</span>
          <span className="text-muted ms-2 fs-12">Choose Mode and Type, enter Value, fetch securities, then Review and Apply.</span>
        </div>
        <div className="d-flex flex-wrap align-items-end gap-2">
          {canApply && <AsyncButton className="btn btn-outline-primary btn-sm" icon="fe fe-save" onClick={() => setTakeBackupOpen(true)}>Take backup</AsyncButton>}
          <div style={{ minWidth: 200 }}>
            <Select2 placeholder="Profiles" allowClear value={profileId} options={(plans.data ?? []).map((p) => ({ value: p.id, label: p.name }))} onChange={onProfileChange} />
          </div>
          {canApply && profileId && (
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setDeleteOpen(true)}>Delete</button>
          )}
        </div>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-lg-4 col-md-6">
          <label className="form-label" title={modeTooltip(mode)}>Mode</label>
          <Select2 searchable={false} allowClear={false} value={mode} options={options.modes.map((m) => ({ value: m.value, label: m.text }))} onChange={(v) => { setMode(v ?? '0'); invalidateReview(); }} />
        </div>
        <div className="col-lg-4 col-md-6">
          <label className="form-label" title={typeTooltip(applyType)}>Type</label>
          <Select2 searchable={false} allowClear placeholder="Select…" value={applyType} options={options.applyTypes.map((t) => ({ value: t.value, label: t.text }))} onChange={(v) => { setApplyType(v ?? ''); invalidateReview(); }} />
        </div>
        <div className="col-lg-4 col-md-6">
          <label className="form-label" title="Unlocked after Type is chosen.">Value</label>
          <input type="number" min="0" step="any" className="form-control" disabled={applyType === ''} value={value} onChange={(e) => { setValue(e.target.value); invalidateReview(); }} />
        </div>
      </div>

      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          <div key="group">
            <label className="form-label" title="Use mask sign '*' to include all and negation sign '!' to exclude groups. Matching is case sensitive.">Group Filter</label>
            <input className="form-control" placeholder={'e.g. real\\AB-*, !*Islamic*'} value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doFetch()} />
          </div>,
          <div key="symbol">
            <label className="form-label">Symbol Filter</label>
            <input className="form-control" placeholder={'e.g. EURUS* or Forex\\EUR*'} value={symbolFilter} onChange={(e) => setSymbolFilter(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doFetch()} />
          </div>,
          <div key="login">
            <label className="form-label" title="Optional. Empty = smallest login in group.">Preview Login</label>
            <input className="form-control" inputMode="numeric" placeholder="e.g. 3020" value={previewLogin} onChange={(e) => { setPreviewLogin(e.target.value); invalidateReview(); }} onKeyDown={(e) => e.key === 'Enter' && doFetch()} />
          </div>,
        ]}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={doFetch} disabled={!applyEnabled}>Fetch</AsyncButton>
            <button type="button" className="btn btn-light btn-sm" onClick={doClear}>Clear</button>
          </>
        }
      />

      {!barHidden && (hasResults || selected.size > 0) && (
        <div className="d-flex flex-wrap align-items-center gap-2 my-2">
          <span className="small text-muted">{selected.size} of {matchedCount} securities selected</span>
          <AsyncButton className="btn btn-primary btn-sm" icon="fe fe-eye" onClick={doReview} disabled={selected.size === 0}>Review</AsyncButton>
          {canApply && <AsyncButton className="btn btn-outline-primary btn-sm" onClick={() => setSaveOpen(true)} disabled={selected.size === 0}>Save</AsyncButton>}
          <AsyncButton className="btn btn-success btn-sm" icon="fa fa-check" onClick={doApply} disabled={applyBtnDisabled}>Apply</AsyncButton>
        </div>
      )}

      <DataGrid
        columns={columns}
        data={gridRows}
        rowKey={(r) => rowKeyOf(r)}
        card
        columnVisibilityToggle={false}
        rowClassName={(r) => (selected.has(rowKeyOf(r)) ? 'table-active' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'Fetch securities to begin.'}
        server={planRows ? undefined : { pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />

      {showReview && reviewed && (
        <ReviewDialog rows={reviewed.rows} mode={reviewed.mode} applyType={reviewed.applyType} rate={reviewed.rate} applyEnabled={applyEnabled} onConfirm={doApply} onClose={() => setShowReview(false)} />
      )}

      <ConfirmDialog
        show={takeBackupOpen}
        title="Take margin rate backup?"
        message="Captures every group/path on the platform right now. Default/inherited rates stay Default (not converted to 1). Restore later from Configurations with Apply backup."
        confirmLabel="Take backup"
        confirmVariant="primary"
        onConfirm={doTakeBackup}
        onCancel={() => setTakeBackupOpen(false)}
      />

      <ConfirmDialog
        show={deleteOpen}
        title="Delete profile?"
        message="Removes this named profile. The current table is left as-is so you can still Review and Apply."
        confirmLabel="Delete"
        onConfirm={doDeleteProfile}
        onCancel={() => setDeleteOpen(false)}
      />

      <Modal
        show={saveOpen}
        title="Save profile?"
        onClose={() => setSaveOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-light btn-sm" onClick={() => setSaveOpen(false)}>Cancel</button>
            <AsyncButton className="btn btn-primary btn-sm" onClick={doSavePlan}>Save</AsyncButton>
          </>
        }
      >
        <label className="form-label">Profile name</label>
        <input className="form-control" maxLength={160} placeholder="Profile name" value={saveName} onChange={(e) => setSaveName(e.target.value)} />
      </Modal>
    </div>
  );
}
